import {
  type Column,
  ExportApiReportFormatters,
} from '@ovComponents/3-pattern/downloadButton/downloadButton';
import dayjs from 'dayjs';

export const PORTFOLIO_EXPORT_QUERY_PATH = 'fetchPortfolioReports';
export const PORTFOLIO_EXPORT_DATASET_PATH = 'portfolioReports';

export const PORTFOLIO_EXPORT_GQL = `
  query fetchPortfolioReports($input: FetchPortfolioReportsInput!) {
    fetchPortfolioReports(input: $input) {
      totalCount
      portfolioReports {
        id
        modelPortfolioType
        state
        marketValueCents
        cashAvailableForTradeCents
        expectedCashPercentage
        driftRebalanceRequired
        pendingSubTradeRequests
        updatedAt
        modelPortfolio {
          translatedName {
            en
          }
        }
        lastOptimizedBy {
          createdAt
        }
      }
    }
  }
`;

export const STATUS_FILTER_MAP: Record<string, string> = {
  DRIFT: 'driftRebalanceRequired',
  TRANSFER: 'transferRebalanceRequired',
  LIQUIDATE: 'liquidateRebalanceRequired',
  ANY_REBALANCE: 'anyRebalanceRequired',
  PENDING_TRADES: 'pendingSubTradeRequests',
};

export type PortfolioExportTranslate = (
  key:
    | 'download.exportHeaders.portfolioId'
    | 'download.exportHeaders.type'
    | 'download.exportHeaders.modelPortfolio'
    | 'download.exportHeaders.marketValue'
    | 'download.exportHeaders.cashAvailable'
    | 'download.exportHeaders.targetCashPercent'
    | 'download.exportHeaders.status'
    | 'download.exportHeaders.driftRequired'
    | 'download.exportHeaders.pendingTrades'
    | 'download.exportHeaders.lastOptimized'
    | 'download.fileNamePrefix',
) => string;

export const getPortfolioExportColumns = (
  t: PortfolioExportTranslate,
): Column[] => [
  { gqlAlias: 'id', header: t('download.exportHeaders.portfolioId') },
  { gqlAlias: 'modelPortfolioType', header: t('download.exportHeaders.type') },
  {
    gqlAlias: 'modelPortfolio.translatedName.en',
    header: t('download.exportHeaders.modelPortfolio'),
  },
  {
    gqlAlias: 'marketValueCents',
    header: t('download.exportHeaders.marketValue'),
    formatter: ExportApiReportFormatters.DIVIDE_BY_100,
  },
  {
    gqlAlias: 'cashAvailableForTradeCents',
    header: t('download.exportHeaders.cashAvailable'),
    formatter: ExportApiReportFormatters.DIVIDE_BY_100,
  },
  {
    gqlAlias: 'expectedCashPercentage',
    header: t('download.exportHeaders.targetCashPercent'),
    formatter: ExportApiReportFormatters.MULTIPLY_BY_100,
  },
  { gqlAlias: 'state', header: t('download.exportHeaders.status') },
  {
    gqlAlias: 'driftRebalanceRequired',
    header: t('download.exportHeaders.driftRequired'),
  },
  {
    gqlAlias: 'pendingSubTradeRequests',
    header: t('download.exportHeaders.pendingTrades'),
  },
  {
    gqlAlias: 'lastOptimizedBy.createdAt',
    header: t('download.exportHeaders.lastOptimized'),
  },
];

export type PortfolioExportFilter = {
  organizationId?: string;
  driftRebalanceRequired?: boolean;
  transferRebalanceRequired?: boolean;
  liquidateRebalanceRequired?: boolean;
  anyRebalanceRequired?: boolean;
  pendingSubTradeRequests?: boolean;
  modelPortfolioType?: string;
  modelPortfolioIds?: string[];
  searchText?: string;
};

const stripUndefined = <T extends Record<string, unknown>>(filter: T): T =>
  Object.fromEntries(
    Object.entries(filter).filter(([, value]) => value !== undefined),
  ) as T;

export const buildExportFilterAll = (
  organizationId: string,
): PortfolioExportFilter => ({
  organizationId,
});

const buildExportFilterFiltered = (
  listFilter: PortfolioExportFilter,
): PortfolioExportFilter => stripUndefined(listFilter);

export const buildExportAdditionalFilter = (
  listFilter: PortfolioExportFilter,
): PortfolioExportFilter => {
  const { organizationId: _organizationId, ...additionalFilter } =
    buildExportFilterFiltered(listFilter);
  return additionalFilter;
};

export const buildPortfolioExportFileName = (
  t: PortfolioExportTranslate,
): string => `${t('download.fileNamePrefix')}-${dayjs().format('YYYY-MM-DD')}`;
